import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Receipt, Trash2, Wallet } from 'lucide-react'
import {
  expenseSchema, formatDateTime, formatMoney, formatNumber, PAYMENT_METHODS, PERMISSIONS, toMinor,
  type ExpenseListItem, type PaymentMethod,
} from '@najah/core'
import {
  Alert, Badge, Button, Card, CardBody, ConfirmDialog, Dialog, EmptyState, Input, Pagination, Select,
  Switch, Textarea, Table, TableSkeleton, TableWrapper, Tbody, Td, Th, Thead, Tr, cn, useToast,
} from '@najah/ui'
import { expensesApi, type ExpenseFilter } from '../lib/purchasingApi'
import { useAuth } from '../lib/auth'
import { useFormErrors } from '../lib/useFormErrors'

function ExpenseFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, validate, handleApiError } = useFormErrors()

  const [categoryId, setCategoryId] = useState('')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('CASH')
  const [docNumber, setDocNumber] = useState('')
  const [note, setNote] = useState('')
  const [fromDrawer, setFromDrawer] = useState(false)
  const [spentAt, setSpentAt] = useState('')

  const { data: categories } = useQuery({ queryKey: ['expenses', 'categories'], queryFn: expensesApi.categories })

  const mutation = useMutation({
    mutationFn: () => {
      const parsed = validate(expenseSchema, {
        categoryId: categoryId || null,
        amount: toMinor(Number(amount) || 0),
        method,
        docNumber: docNumber.trim(),
        note: note.trim(),
        fromDrawer,
        spentAt: spentAt || undefined,
      })
      if (!parsed) throw new Error('VALIDATION')
      return expensesApi.create(parsed)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] })
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success('سُجّل المصروف', `${res.reference} — ${formatMoney(res.amount)}`)
      setAmount('')
      setNote('')
      setFromDrawer(false)
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّر تسجيل المصروف')
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="مصروف جديد"
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>تسجيل</Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="البند" required
            value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
            options={(categories ?? []).map((c) => ({ value: c.id, label: c.nameAr }))}
            placeholder="اختر بنداً"
            error={errors.categoryId}
          />
          <Input
            label="المبلغ" required type="number" min="0" step="0.01" ltr autoFocus
            value={amount} onChange={(e) => setAmount(e.target.value)}
            error={errors.amount} hint="دج"
          />
          <Select
            label="طريقة الدفع"
            value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            options={Object.entries(PAYMENT_METHODS)
              .filter(([k]) => k !== 'CREDIT')
              .map(([value, label]) => ({ value, label }))}
          />
          <Input
            label="التاريخ" type="date" ltr
            value={spentAt} onChange={(e) => setSpentAt(e.target.value)}
            hint="اتركه فارغاً لليوم"
          />
        </div>

        <Input
          label="رقم الوصل" ltr
          value={docNumber} onChange={(e) => setDocNumber(e.target.value)}
          error={errors.docNumber}
        />
        <Textarea label="ملاحظة" rows={2} value={note} onChange={(e) => setNote(e.target.value)} error={errors.note} />

        <Switch
          checked={fromDrawer}
          onChange={(e) => setFromDrawer(e.target.checked)}
          disabled={method !== 'CASH'}
          label="خصم من درج الصندوق"
          description={method !== 'CASH' ? 'متاح للدفع النقدي فقط' : 'يتطلّب وردية مفتوحة'}
        />
      </div>
    </Dialog>
  )
}

export function ExpensesPage() {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can(PERMISSIONS.PURCHASE_MANAGE)

  const [filter, setFilter] = useState<ExpenseFilter>({ page: 1, pageSize: 25 })
  const [formOpen, setFormOpen] = useState(false)
  const [deleting, setDeleting] = useState<ExpenseListItem | null>(null)

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['expenses', filter],
    queryFn: () => expensesApi.list(filter),
    placeholderData: keepPreviousData,
  })
  const { data: categories } = useQuery({ queryKey: ['expenses', 'categories'], queryFn: expensesApi.categories })

  const removeMutation = useMutation({
    mutationFn: (id: string) => expensesApi.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses'] })
      void queryClient.invalidateQueries({ queryKey: ['shift'] })
      toast.success('حُذف المصروف')
      setDeleting(null)
    },
    onError: () => {
      toast.error('تعذّر الحذف')
      setDeleting(null)
    },
  })

  function patch(next: ExpenseFilter) {
    setFilter((f) => ({ ...f, ...next, page: next.page ?? 1 }))
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">المصاريف</h1>
          <p className="mt-0.5 text-sm text-fg-muted">المصاريف التشغيلية خارج المشتريات</p>
        </div>
        {canManage && (
          <Button startIcon={<Plus className="size-4" />} onClick={() => setFormOpen(true)}>
            مصروف جديد
          </Button>
        )}
      </div>

      {data && (
        <Card>
          <CardBody className="flex items-center gap-4">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-danger-subtle text-danger">
              <Wallet className="size-5" />
            </span>
            <div>
              <p className="text-sm text-fg-muted">إجمالي المصاريف في الفترة المحدّدة</p>
              <p className="mt-0.5 text-2xl font-bold text-fg" dir="ltr">{formatMoney(data.totalAmount)}</p>
              <p className="mt-0.5 text-xs text-fg-subtle">{formatNumber(data.total)} مصروفاً</p>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-3">
        <Select
          label="البند"
          value={filter.categoryId ?? ''}
          onChange={(e) => patch({ categoryId: e.target.value || undefined })}
          options={(categories ?? []).map((c) => ({ value: c.id, label: c.nameAr }))}
          placeholder="كل البنود"
        />
        <Input
          label="من تاريخ" type="date" ltr
          value={filter.from ?? ''}
          onChange={(e) => patch({ from: e.target.value || undefined })}
        />
        <Input
          label="إلى تاريخ" type="date" ltr
          value={filter.to ?? ''}
          onChange={(e) => patch({ to: e.target.value || undefined })}
        />
      </div>

      <TableWrapper className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
        <Table>
          <Thead>
            <tr>
              <Th>المرجع</Th>
              <Th>البند</Th>
              <Th>التاريخ</Th>
              <Th>الدفع</Th>
              <Th>ملاحظة</Th>
              <Th align="end">المبلغ</Th>
              {canManage && <Th align="center"></Th>}
            </tr>
          </Thead>

          {isPending ? (
            <TableSkeleton rows={6} cols={7} />
          ) : (
            <Tbody>
              {data?.items.map((e) => (
                <Tr key={e.id}>
                  <Td className="font-mono text-xs text-fg-muted" dir="ltr">{e.reference}</Td>
                  <Td>
                    {e.categoryName ? (
                      <Badge tone="neutral">{e.categoryName}</Badge>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap text-xs text-fg-muted">{formatDateTime(e.spentAt)}</Td>
                  <Td className="text-sm text-fg-muted">
                    {PAYMENT_METHODS[e.method as PaymentMethod] ?? e.method}
                  </Td>
                  <Td className="max-w-xs truncate text-xs text-fg-subtle" title={e.note ?? undefined}>
                    {e.note ?? '—'}
                  </Td>
                  <Td align="end" className="font-semibold tabular-nums text-danger" dir="ltr">
                    {formatMoney(e.amount)}
                  </Td>
                  {canManage && (
                    <Td align="center">
                      <Button
                        variant="ghost" size="icon" title="حذف"
                        className="text-danger hover:bg-danger-subtle"
                        onClick={() => setDeleting(e)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>

        {!isPending && data?.items.length === 0 && (
          <EmptyState
            icon={<Receipt className="size-10" />}
            title="لا توجد مصاريف"
            description="سجّل الإيجار والكهرباء والنقل وغيرها لتعرف ربحك الحقيقي"
            action={canManage ? <Button onClick={() => setFormOpen(true)}>مصروف جديد</Button> : undefined}
          />
        )}

        {data && data.items.length > 0 && (
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            pageSize={data.pageSize}
            onPageChange={(page) => setFilter((f) => ({ ...f, page }))}
            onPageSizeChange={(pageSize) => patch({ pageSize })}
          />
        )}
      </TableWrapper>

      <ExpenseFormDialog open={formOpen} onClose={() => setFormOpen(false)} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && removeMutation.mutate(deleting.id)}
        loading={removeMutation.isPending}
        title="حذف المصروف"
        confirmLabel="حذف"
        description={
          <>
            سيُحذف <span className="font-semibold text-fg" dir="ltr">{deleting?.reference}</span> بمبلغ{' '}
            <span className="font-semibold text-fg" dir="ltr">{deleting && formatMoney(deleting.amount)}</span>.
            <br />
            إن كان مخصوماً من درج صندوق مفتوح فسيُعاد المبلغ إليه.
          </>
        }
      />
    </div>
  )
}
